import { X as CloseIcon, Loader2 } from "lucide-react";
import type { FormEvent, ReactNode } from "react";

// ---------------------------------------------------------------------------
// FormModal — standard modal shell for form-based dialogs
// ---------------------------------------------------------------------------

interface FormModalProps {
  title: string;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  children: ReactNode;
  /** Footer content — if omitted, no footer is rendered (you handle your own buttons) */
  footer?: ReactNode;
  maxWidth?: string;
}

export function FormModal({
  title,
  loading = false,
  onClose,
  onSubmit,
  children,
  footer,
  maxWidth = "max-w-2xl",
}: Readonly<FormModalProps>) {
  return (
    <div className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50 p-4">
      <div
        className={`bg-surface-primary rounded-xl border border-border-primary w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-surface-primary border-b border-border-primary px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-content-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-interactive-hover rounded-lg transition-colors"
          >
            <CloseIcon className="w-5 h-5 text-content-tertiary" />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-6 space-y-4">
            {children}
            {footer}
          </form>
        )}
      </div>
    </div>
  );
}
